import {
  addDays,
  planForTrigger,
  topTriggers,
  TRIGGER_LABELS,
  TRIGGERS,
  type Trigger,
} from '@tahi/core';
import { useState } from 'react';
import { setTriggerPlan } from '../../../data/commands/quit';
import { useCommandEnv } from '../../../data/hooks';
import { Button, ScreenHeader } from '../../../ui';
import { showToast } from '../../../ui/toast';
import { copy, errorText, QUIT_BACK } from '../copy';
import { useQuitData, useQuitToday } from '../hooks';
import { CARD, INPUT, LABEL, QUIET_LINK, SCREEN } from '../styles';

/** Top triggers over 30 days first, then the rest, each with a simple plan the user can rewrite. */
export function TriggersScreen() {
  const data = useQuitData();
  const today = useQuitToday();
  const env = useCommandEnv();
  const [editing, setEditing] = useState<Trigger | null>(null);
  const [text, setText] = useState('');
  if (!data) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.triggersTitle} back={QUIT_BACK} />
        <p role="status" className="text-muted">
          {copy.loading}
        </p>
      </main>
    );
  }
  const stats = topTriggers(data.logs, data.cravings, { start: addDays(today, -29), end: today });
  const order: Trigger[] = [
    ...stats.map((s) => s.trigger),
    ...TRIGGERS.filter((t) => !stats.some((s) => s.trigger === t)),
  ];
  const userPlans = data.plan?.triggerPlans ?? {};

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.triggersTitle} back={QUIT_BACK} />
      <p className={LABEL}>{copy.last30}</p>
      {stats.length === 0 ? <p className="text-muted">{copy.triggersEmpty}</p> : null}
      {order.map((t) => {
        const s = stats.find((x) => x.trigger === t);
        const label = TRIGGER_LABELS[t];
        return (
          <section key={t} aria-label={label} className={CARD}>
            <h2 className="text-lg font-bold text-fg">{label}</h2>
            {s ? (
              <p className="text-sm text-muted">{copy.triggerStats(s.smoked, s.resisted)}</p>
            ) : null}
            {editing === t ? (
              <div className="mt-2 flex flex-col gap-2">
                <textarea
                  aria-label={copy.editTriggerPlan(label)}
                  value={text}
                  maxLength={200}
                  onChange={(e) => setText(e.target.value)}
                  className={`${INPUT} min-h-24 py-2`}
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={async () => {
                      const r = await setTriggerPlan(env, t, text);
                      if (!r.ok) return showToast({ message: errorText(r.error) });
                      setEditing(null);
                    }}
                  >
                    {copy.save}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(null)}>
                    {copy.cancel}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <p className={LABEL + ' mt-3'}>{copy.yourPlan}</p>
                <p className="text-fg">{planForTrigger(t, userPlans)}</p>
                <button
                  type="button"
                  aria-label={copy.editTriggerPlan(label)}
                  className={`${QUIET_LINK} px-0`}
                  onClick={() => {
                    setText(planForTrigger(t, userPlans));
                    setEditing(t);
                  }}
                >
                  {copy.editPlan}
                </button>
              </>
            )}
          </section>
        );
      })}
    </main>
  );
}
